import React, { Fragment, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import LazyLoad from 'react-lazyload';

import GtmService from 'static/script/app/ui/google-tagmanager-service';
import { getCategoryIcon } from 'tradera-helpers/category-icon-helper';
import { usePageMeta } from 'tradera-hooks';
import { useTranslator } from 'tradera-lang';
import Ad from 'tradera-components/ads/ad';
import ALink from 'tradera-components/alink';
import Breadcrumbs from 'tradera-components/breadcrumbs/breadcrumbs';
import Loader from 'tradera-components/loader/loader';
import SiteSubheader from 'tradera-components/site-subheader/site-subheader';
import ArrowDownIcon from '@tradera/blueprint/theme/icons2/arrow-down.svg?jsx';
import ArrowUpIcon from '@tradera/blueprint/theme/icons2/arrow-up.svg?jsx';
import CloseIcon from '@tradera/blueprint/theme/icons2/close.svg?jsx';
import SearchIcon from '@tradera/blueprint/theme/icons2/search.svg?jsx';
import categoryActions from 'tradera-state/categories/actions';
import './category-page.scss';

const CategoryPage = props => {
    const {
        categoryList,
        fetchCategoryList,
        inSPA,
        location,
        pageType
    } = props;
    const [isLoading, setIsLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const [filteredCategories, setFilteredCategories] = useState([]);
    const [openCategories, setOpenCategories] = useState([]);
    const backToTopButtonRef = useRef(null);
    const { t } = useTranslator();
    usePageMeta(pageType);

    // Initial category fetch
    useEffect(() => {
        setIsLoading(true);
        fetchCategoryList()
            .then(() => setIsLoading(false))
            .catch(() => {});
    }, [fetchCategoryList]);

    // Filter categories when changed
    useEffect(() => {
        setFilteredCategories(filterCategories(categoryList, filter));
    }, [categoryList, filter]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    });

    const handleFilterChange = event => {
        setFilter(event.target.value.trim());
    };

    const handleFilterKey = event => {
        const { key, target } = event;
        switch (key) {
            case 'Escape':
                setFilter('');
                break;
            case 'Enter':
                target.blur();
                break;
        }
    };

    const handleScroll = () => {
        if (backToTopButtonRef.current) {
            backToTopButtonRef.current.classList.toggle(
                'd-none',
                window.scrollY < 500
            );
        }
    };

    const toggleCategory = toggle => {
        if (openCategories.includes(toggle.id)) {
            setOpenCategories(openCategories.filter(id => id !== toggle.id));
        } else {
            setOpenCategories([...openCategories, toggle.id]);
        }
    };

    return (
        filteredCategories && (
            <Fragment>
                <SiteSubheader>
                    <Breadcrumbs
                        categoryPath={[
                            {
                                id: 1,
                                name: t('categoryListView_categories'),
                                url: location.pathname
                            }
                        ]}
                        inSPA={inSPA}
                    />
                </SiteSubheader>
                {isLoading && <Loader usePortal={false} />}
                <Ad
                    className="my-3"
                    adFormats="mobile_rectangle,medium_rectangle,mobile_wideboard"
                    deviceName="mobile"
                    position="top-mobile">
                    <div className="ad-placeholder-mobile-320-320" />
                </Ad>
                <div className="category-list container position-relative">
                    <div className="row">
                        <h1
                            className="col-12 text-styled h2 mt-4"
                            id="top"
                            data-scroll-target="top">
                            {filter
                                ? t('categoryListView_categoriesMatching', {
                                      filter
                                  })
                                : t('categoryListView_categories')}
                        </h1>
                    </div>
                    <div className="row">
                        <div className="col-12 col-md-3">
                            <div className="form-control-with-icon mr-md-4">
                                <input
                                    type="text"
                                    className="form-control my-3"
                                    placeholder={t(
                                        'categoryListView_searchPlaceHolder'
                                    )}
                                    value={filter}
                                    onKeyDown={handleFilterKey}
                                    onChange={handleFilterChange}
                                    onBlur={handleFilterBlur}
                                />
                                {filter.length > 0 ? (
                                    <CloseIcon
                                        className="icon icon-dark cursor-pointer pointer-events"
                                        onClick={() => setFilter('')}
                                    />
                                ) : (
                                    <SearchIcon className="icon icon-dark" />
                                )}
                            </div>

                            <ul className="list-unstyled mb-0 d-none d-md-block">
                                {filteredCategories.length > 0 ? (
                                    filteredCategories.map(category => (
                                        <li
                                            key={category.title}
                                            className="py-2">
                                            <ALink
                                                useLink={inSPA}
                                                href={'#' + category.title}
                                                data-scroll-to={category.title}
                                                onClick={scrollTo}>
                                                {category.title}
                                            </ALink>
                                        </li>
                                    ))
                                ) : (
                                    <p>
                                        {t(
                                            'categoryListView_noMatchingCategories'
                                        )}
                                    </p>
                                )}
                            </ul>
                        </div>
                        <div className="col-md-9">
                            {filteredCategories.length > 0 ? (
                                <ul className="list-unstyled mb-0">
                                    {filteredCategories.map(
                                        (category, index) => (
                                            <li
                                                key={category.title}
                                                className="py-0"
                                                data-scroll-target={
                                                    category.title
                                                }>
                                                <h4
                                                    id={category.title}
                                                    className="text-styled category-title my-3 d-md-block d-none">
                                                    {category.title}
                                                </h4>
                                                <div className="d-md-none">
                                                    <div
                                                        className={`d-flex align-items-center ${
                                                            filter
                                                                ? ''
                                                                : 'cursor-pointer'
                                                        }`}
                                                        onClick={() =>
                                                            toggleCategory(
                                                                category
                                                            )
                                                        }>
                                                        <div className="flex-grow-0 flex-shrink-0 p-3 mr-3 bg-light">
                                                            <span className="d-flex align-items-center justify-content-center">
                                                                <LazyLoad
                                                                    placeholder={
                                                                        <div className="icon icon-lg" />
                                                                    }
                                                                    offset={0}
                                                                    once>
                                                                    {getCategoryIcon(
                                                                        category.id,
                                                                        category.title
                                                                    )}
                                                                </LazyLoad>
                                                            </span>
                                                        </div>
                                                        <p
                                                            id={category.title}
                                                            className={`flex-grow-1 flex-shrink-1 my-3 ${
                                                                openCategories.includes(
                                                                    category.id
                                                                )
                                                                    ? 'font-weight-bold'
                                                                    : ''
                                                            }`}>
                                                            <strong>
                                                                {category.title}
                                                            </strong>
                                                        </p>
                                                        <div className="flex-grow-0 flex-shrink-0 p-3">
                                                            {!filter && (
                                                                <Fragment>
                                                                    {openCategories.includes(
                                                                        category.id
                                                                    ) ? (
                                                                        <ArrowUpIcon className="icon" />
                                                                    ) : (
                                                                        <ArrowDownIcon className="icon" />
                                                                    )}
                                                                </Fragment>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <ul
                                                    className={`category-groups list-unstyled text-columns-lg-4 text-columns-sm-2 my-3 ${
                                                        filter ||
                                                        openCategories.includes(
                                                            category.id
                                                        )
                                                            ? ''
                                                            : 'd-none d-md-block'
                                                    }`}>
                                                    {category.categoryNodes.map(
                                                        group => (
                                                            <li
                                                                key={
                                                                    group.title
                                                                }
                                                                className={
                                                                    group.isTopLevel
                                                                        ? 'my-3'
                                                                        : 'py-2'
                                                                }
                                                                style={
                                                                    group.isTopLevel
                                                                        ? {
                                                                              columnSpan:
                                                                                  'all'
                                                                          }
                                                                        : null
                                                                }>
                                                                <ALink
                                                                    useLink={
                                                                        inSPA &&
                                                                        group.IsSpaRoute
                                                                    }
                                                                    className={
                                                                        group.isTopLevel
                                                                            ? 'btn btn-outline-dark'
                                                                            : ''
                                                                    }
                                                                    href={
                                                                        group.href
                                                                    }>
                                                                    {
                                                                        group.title
                                                                    }
                                                                    {group.isTopLevel && (
                                                                        <span>
                                                                            &nbsp;—&gt;
                                                                        </span>
                                                                    )}
                                                                </ALink>
                                                            </li>
                                                        )
                                                    )}
                                                </ul>
                                                {index <
                                                    filteredCategories.length -
                                                        1 && (
                                                    <div className="border-bottom my-0 my-md-5" />
                                                )}
                                            </li>
                                        )
                                    )}
                                </ul>
                            ) : (
                                <p>
                                    {t('categoryListView_noMatchingCategories')}
                                </p>
                            )}
                        </div>
                    </div>
                    <a
                        role="button"
                        href="#top"
                        className="btn btn-primary btn-shadow btn-floating d-none"
                        data-scroll-to="top"
                        onClick={scrollTo}
                        ref={backToTopButtonRef}>
                        <span className="d-none d-md-inline">
                            {t('categoryListView_backToTop')}
                        </span>
                        <ArrowUpIcon className="icon icon-white icon-after" />
                    </a>
                </div>
            </Fragment>
        )
    );
};
CategoryPage.propTypes = {
    categoryList: PropTypes.arrayOf(PropTypes.object),
    inSPA: PropTypes.bool,
    fetchCategoryList: PropTypes.func.isRequired
};
const mapStateToProps = state => ({
    categoryList: state.categories.list,
    inSPA: state.environment.featureSwitches['spa']
});
const mapDispatchToProps = dispatch => ({
    fetchCategoryList: () => dispatch(categoryActions.fetchCategoryList())
});

function filterCategories(categories, filter) {
    if (categories && filter.length > 0) {
        return categories
            .slice()
            .map(category => {
                const categoryNodes = category.categoryNodes
                    .slice()
                    .filter(node =>
                        node.title.toLowerCase().includes(filter.toLowerCase())
                    );
                return {
                    ...category,
                    categoryNodes
                };
            })
            .filter(category => category.categoryNodes.length > 0);
    }
    return categories;
}

const handleFilterBlur = event => {
    const {
        target: { value }
    } = event;
    if (value && value.length > 0) {
        GtmService.trackAction(
            'Category browse',
            'SearchCategories',
            `Search: ${value}`
        );
    }
};

const scrollTo = event => {
    const scrollTarget = event.currentTarget.dataset.scrollTo;
    const scrollToElement = document.body.querySelector(
        `[data-scroll-target="${scrollTarget}"]`
    );
    if (scrollToElement) {
        event.preventDefault();
        let { top } = scrollToElement.getBoundingClientRect();
        top = Math.max(0, top - 50);
        window.scrollTo(0, top);
    }
};

export default connect(mapStateToProps, mapDispatchToProps)(CategoryPage);
export { filterCategories };
